import { useState } from 'react'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@apollo/client'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert, Row, Col } from 'reactstrap'
import Select from 'react-select'
import './Modals.css'

const UpdateIncident = gql`
    mutation updateIncident($input: UpdateIncidentMutationInput!) {
        updateIncident(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

const UpdateIncidentPartyPeople = gql`
    mutation updateIncidentPartyPeopleMutation($input: UpdateIncidentPartyPeopleMutationInput!) {
        updateIncidentPartyPeople(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

const AllDrivers = gql`
    query AllDrivers($searchTerm: String, $branchIds: [ID]) {
        drivers: allDrivers(searchTerm: $searchTerm, branchIds: $branchIds, first: 10) {
            edges {
                node {
                    id
                    pk
                    name
                    email
                    phone
                    address
                    dmvLicense
                    branch {
                        id
                        name
                    }
                }
            }
        }
    }
`

const getMutationError = (payload, fallback) => {
    const message = payload?.errors?.[0]?.messages?.[0] || payload?.errors?.[0]?.messages || payload?.errors
    return message || fallback
}

const ChangeDriverModal = ({ isOpen, toggle, incidentId, partyPeople, incidentBranchIds = null, refetchQuery }) => {
    const [error, setError] = useState(null)
    const [selectedDriver, setSelectedDriver] = useState(null)
    const [driverSearchTerm, setDriverSearchTerm] = useState('')
    const [showDriverDropdown, setShowDriverDropdown] = useState(false)
    const driverSearchBranchIds = incidentBranchIds && incidentBranchIds.length > 0 ? incidentBranchIds : null
    const roleOption = { value: 'VEHICLE_DRIVER', label: 'Vehicle Driver' }

    const { data: driversData, loading: driversLoading } = useQuery(AllDrivers, {
        variables: { searchTerm: driverSearchTerm || null, branchIds: driverSearchBranchIds },
        skip: !isOpen,
        fetchPolicy: 'network-only',
    })
    const [updateIncident, { loading: updateIncidentLoading }] = useMutation(UpdateIncident)
    const [updateIncidentPartyPeople, { loading: updatePartyPeopleLoading }] = useMutation(UpdateIncidentPartyPeople)
    const driverSuggestions = driversData?.drivers?.edges || []
    const isSubmitting = updateIncidentLoading || updatePartyPeopleLoading

    const resetLocalState = () => {
        setError(null)
        setSelectedDriver(null)
        setDriverSearchTerm('')
        setShowDriverDropdown(false)
    }

    const closeAndReset = () => {
        resetLocalState()
        toggle()
    }

    const handleDriverSelect = (driver) => {
        setSelectedDriver(driver || null)
        setDriverSearchTerm(driver?.name || '')
        setShowDriverDropdown(false)
        setError(null)
    }

    const handleDriverSearchInput = (event) => {
        setDriverSearchTerm(event.target.value)
        setSelectedDriver(null)
        setShowDriverDropdown(true)
        setError(null)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        setError(null)

        if (!selectedDriver?.id) {
            setError('Driver is required')
            return
        }
        if (!incidentId || !partyPeople?.id) {
            setError('Missing incident or party person.')
            return
        }

        try {
            const incidentRes = await updateIncident({
                variables: {
                    input: {
                        incidentId,
                        driverId: selectedDriver.id,
                    },
                },
            })
            const incidentPayload = incidentRes?.data?.updateIncident
            if (!incidentPayload?.ok) {
                setError(getMutationError(incidentPayload, 'Could not update incident driver'))
                return
            }

            const partyPeopleRes = await updateIncidentPartyPeople({
                variables: {
                    input: {
                        partyPeopleId: partyPeople.id,
                        role: 'VEHICLE_DRIVER',
                        name: selectedDriver?.name || '',
                        phoneNumber: selectedDriver?.phone || '',
                        email: selectedDriver?.email || '',
                        address: selectedDriver?.address || '',
                        driverLicense: selectedDriver?.dmvLicense || '',
                    },
                },
            })
            const partyPeoplePayload = partyPeopleRes?.data?.updateIncidentPartyPeople
            if (!partyPeoplePayload?.ok) {
                setError(getMutationError(partyPeoplePayload, 'Could not update party driver details'))
                return
            }

            if (refetchQuery) await refetchQuery()
            closeAndReset()
        } catch (err) {
            setError('An unexpected error occurred. ' + err.message)
        }
    }

    return (
        <Modal className='add-people-modal modal-dialog-centered' isOpen={isOpen} toggle={closeAndReset} size="lg">
            <ModalHeader>Change Driver</ModalHeader>
            <form onSubmit={handleSubmit}>
                <ModalBody>
                    {error && <Row><Col><Alert color="danger">{error}</Alert></Col></Row>}
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label>Current Driver</Label>
                                <Input type="text" value={partyPeople?.name || '--'} disabled readOnly />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label for="changeDriverRole">Role*</Label>
                                <Select
                                    className="bos-custom-select"
                                    classNamePrefix="bos-select"
                                    inputId="changeDriverRole"
                                    options={[roleOption]}
                                    value={roleOption}
                                    isDisabled
                                    isSearchable={false}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label for="changeDriverSearch">Select Driver*</Label>
                                <Input
                                    id="changeDriverSearch"
                                    type="text"
                                    value={driverSearchTerm}
                                    onChange={handleDriverSearchInput}
                                    onFocus={() => setShowDriverDropdown(true)}
                                    placeholder="Search by driver name, tlc, phone, email"
                                    autoComplete="off"
                                />
                                {showDriverDropdown && (
                                    <div className="option-list car-search-dropdown">
                                        {driversLoading ? (
                                            <p className="p-2 mb-0 text-center text-muted">Loading drivers, please wait...</p>
                                        ) : driverSuggestions.length > 0 ? (
                                            driverSuggestions.map((driver, idx) => (
                                                <Row key={idx} className="option py-1" style={{ cursor: 'pointer' }} onClick={() => handleDriverSelect(driver.node)}>
                                                    <Col>
                                                        <p className="mb-0" style={{ fontWeight: 500 }}>
                                                            {driver.node.name || `Driver #${driver.node.pk || ''}`}<br />
                                                            <small>{[driver.node.phone, driver.node.dmvLicense, driver.node.branch?.name].filter(Boolean).join(' - ') || driver.node.email || ''}</small>
                                                        </p>
                                                    </Col>
                                                </Row>
                                            ))
                                        ) : (
                                            <p className="p-2 mb-0 text-center text-muted">No drivers found.</p>
                                        )}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAndReset} disabled={isSubmitting}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Loading...' : 'Change Driver'}</Button>
                </ModalFooter>
            </form>
        </Modal>
    )
}

export default ChangeDriverModal
